// src/services/financeService.js - COMPLETE SINGLE SOURCE OF TRUTH
/**
 * Single source of truth for all financial calculations
 * Used by both vendor and admin pages
 */

// Ethiopian Banks List - EXACT MATCH with backend
export const ETHIOPIAN_BANKS = [
  { code: "CBET", name: "Commercial Bank of Ethiopia" },
  { code: "BOA", name: "Bank of Abyssinia" },
  { code: "AWASH", name: "Awash Bank" },
  { code: "DASHEN", name: "Dashen Bank" },
  { code: "WEGAGEN", name: "Wegagen Bank" },
  { code: "UNITED", name: "United Bank" },
  { code: "NIB", name: "NIB International Bank" },
  { code: "ZEMEN", name: "Zemen Bank" },
  { code: "OROMIA", name: "Oromia Bank" },
  { code: "BERHAN", name: "Berhan Bank" },
  { code: "ABAY", name: "Abay Bank" },
  { code: "AMHARA", name: "Amhara Bank" },
  { code: "ADDIS", name: "Addis International Bank" },
  { code: "DEBUB", name: "Debub Global Bank" },
  { code: "BUNNA", name: "Bunna Bank" },
  { code: "ENAT", name: "Enat Bank" },
  { code: "TSEDEY", name: "Tsedey Bank" },
  { code: "SIINQEE", name: "Siinqee Bank" },
  { code: "GOH", name: "Goh Betoch Bank" },
];

// Mobile Money Providers
export const MOBILE_MONEY_PROVIDERS = [
  { code: "TELECASH", name: "TeleBirr (Ethio Telecom)" },
  { code: "SAFARICOM", name: "Safaricom M-PESA" },
  { code: "MPESA", name: "M-PESA" },
];

// Format currency consistently
export const formatCurrency = (amount) => {
  const num = typeof amount === 'string' ? parseFloat(amount) : (amount || 0);
  return `ETB ${num.toFixed(2)}`;
};

// Format date consistently
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Get status color consistently
export const getStatusColor = (status) => {
  const colors = {
    pending: 'warning',
    processing: 'info',
    completed: 'success',
    paid: 'success',
    success: 'success',
    failed: 'error',
    cancelled: 'default',
    refunded: 'default',
  };
  return colors[status?.toLowerCase()] || 'default';
};

// Get status icon consistently
export const getStatusIcon = (status) => {
  const icons = {
    pending: '⏳',
    processing: '🔄',
    completed: '✅',
    paid: '✅',
    success: '✅',
    failed: '❌',
    cancelled: '🚫',
    refunded: '↩️',
  };
  return icons[status?.toLowerCase()] || '⏳';
};

// Get status chip props
export const getStatusChipProps = (status) => {
  const safeStatus = status?.toLowerCase() || 'pending';
  const config = {
    pending: { color: 'warning', label: 'PENDING' },
    processing: { color: 'info', label: 'PROCESSING' },
    completed: { color: 'success', label: 'COMPLETED' },
    paid: { color: 'success', label: 'PAID' },
    success: { color: 'success', label: 'SUCCESS' },
    failed: { color: 'error', label: 'FAILED' },
    cancelled: { color: 'default', label: 'CANCELLED' },
    refunded: { color: 'default', label: 'REFUNDED' },
  };
  return config[safeStatus] || config.pending;
};

// Get transaction type icon
export const getTransactionTypeIcon = (type) => {
  const icons = {
    deposit: '📈',
    withdrawal: '📉',
    payment: '💳',
    payout: '🏦',
    refund: '↩️',
    commission: '📊',
    order_payment: '🛒',
    transfer_received: '📥',
    transfer_sent: '📤',
  };
  return icons[type] || '📝';
};

// Get transaction type label
export const getTransactionTypeLabel = (type) => {
  const labels = {
    deposit: 'Deposit',
    withdrawal: 'Withdrawal',
    payment: 'Payment',
    payout: 'Payout',
    refund: 'Refund',
    commission: 'Commission',
    order_payment: 'Order Payment',
    transfer_received: 'Transfer Received',
    transfer_sent: 'Transfer Sent',
  };
  return labels[type] || type?.replace('_', ' ') || 'N/A';
};

// Get payout status info
export const getPayoutStatusInfo = (status) => {
  const safeStatus = status?.toLowerCase() || 'pending';
  const config = {
    pending: { label: 'Pending', color: 'warning', icon: '⏳' },
    processing: { label: 'Processing', color: 'info', icon: '🔄' },
    completed: { label: 'Completed', color: 'success', icon: '✅' },
    paid: { label: 'Paid', color: 'success', icon: '✅' },
    success: { label: 'Success', color: 'success', icon: '✅' },
    failed: { label: 'Failed', color: 'error', icon: '❌' },
    cancelled: { label: 'Cancelled', color: 'default', icon: '🚫' },
  };
  return config[safeStatus] || config.pending;
};

// ============================================================
// FINANCIAL CALCULATIONS - SINGLE SOURCE OF TRUTH
// ============================================================

/**
 * Calculate financial summary from transactions
 * This is the SINGLE SOURCE OF TRUTH for all financial calculations
 */
export const calculateFinancialSummary = (transactions = []) => {
  let totalRevenue = 0;
  let totalCommission = 0;
  let totalPayouts = 0;
  let pendingPayouts = 0;
  let processingPayouts = 0;
  let totalWithdrawn = 0;

  transactions.forEach(tx => {
    const amount = parseFloat(tx.amount) || 0;
    const status = tx.status?.toLowerCase() || '';
    const type = tx.type?.toLowerCase() || '';

    // Revenue from completed payments
    if ((type === 'payment' || type === 'order_payment' || type === 'deposit') && 
        (status === 'success' || status === 'completed' || status === 'paid')) {
      totalRevenue += amount;
    }

    // Commission from completed commissions
    if (type === 'commission' && 
        (status === 'success' || status === 'completed')) {
      totalCommission += amount;
    }

    // Payouts / Withdrawals
    if (type === 'payout' || type === 'withdrawal') {
      if (status === 'completed' || status === 'success' || status === 'paid') {
        totalPayouts += amount;
        totalWithdrawn += amount;
      } else if (status === 'pending') {
        pendingPayouts += amount;
      } else if (status === 'processing') {
        processingPayouts += amount;
      }
    }
  });

  // Available balance = Revenue - Commission - Completed Payouts - Pending - Processing
  const availableBalance = Math.max(0, totalRevenue - totalCommission - totalPayouts - pendingPayouts - processingPayouts);

  return {
    totalRevenue,
    totalCommission,
    totalPayouts,
    totalWithdrawn,
    pendingPayouts,
    processingPayouts,
    availableBalance,
    totalTransactions: transactions.length,
  };
};

/**
 * Calculate payout statistics from payouts list
 */
export const calculatePayoutStats = (payouts = []) => {
  let pendingCount = 0;
  let totalPendingAmount = 0;
  let completedCount = 0;
  let totalCompletedAmount = 0;
  let processingCount = 0;
  let totalProcessingAmount = 0;
  let failedCount = 0;
  let totalFailedAmount = 0;

  payouts.forEach(p => {
    const amount = parseFloat(p.amount) || 0;
    const status = p.status?.toLowerCase() || '';

    switch (status) {
      case 'pending':
        pendingCount++;
        totalPendingAmount += amount;
        break;
      case 'processing':
        processingCount++;
        totalProcessingAmount += amount;
        break;
      case 'completed':
      case 'paid':
      case 'success':
        completedCount++;
        totalCompletedAmount += amount;
        break;
      case 'failed':
      case 'cancelled':
        failedCount++;
        totalFailedAmount += amount;
        break;
      default:
        break;
    }
  });

  return {
    pendingCount,
    totalPendingAmount,
    completedCount,
    totalCompletedAmount,
    processingCount,
    totalProcessingAmount,
    failedCount,
    totalFailedAmount,
    totalPayouts: payouts.length,
    totalAmount: totalCompletedAmount + totalPendingAmount + totalProcessingAmount,
  };
};

// Helper to get bank name from code
export const getBankNameFromCode = (code) => {
  const bank = ETHIOPIAN_BANKS.find(b => b.code === code);
  return bank ? bank.name : code || "";
};

// Helper to get bank code from name
export const getBankCodeFromName = (name) => {
  const bank = ETHIOPIAN_BANKS.find(b => b.name === name);
  return bank ? bank.code : name || "";
};

// Helper to get provider name from code
export const getProviderNameFromCode = (code) => {
  const provider = MOBILE_MONEY_PROVIDERS.find(p => p.code === code);
  return provider ? provider.name : code || "";
};

// ============================================================
// PAYOUT METHODS - SINGLE SOURCE OF TRUTH
// ============================================================

export const PAYOUT_METHODS = [
  { id: 'bank_transfer', name: 'Bank Transfer', icon: '🏦', requiresBank: true, requiresPhone: false },
  { id: 'telebirr', name: 'TeleBirr', icon: '📱', requiresBank: false, requiresPhone: true },
  { id: 'mpesa', name: 'M-Pesa', icon: '📱', requiresBank: false, requiresPhone: true },
  { id: 'cbebirr', name: 'CBE Birr', icon: '📱', requiresBank: false, requiresPhone: true },
  { id: 'awashbirr', name: 'Awash Birr', icon: '📱', requiresBank: false, requiresPhone: true },
  { id: 'ebirr', name: 'eBirr', icon: '📱', requiresBank: false, requiresPhone: true },
  { id: 'yaya', name: 'Yaya Wallet', icon: '📱', requiresBank: false, requiresPhone: true },
  { id: 'coopay', name: 'Coopay', icon: '🏦', requiresBank: true, requiresPhone: false },
  { id: 'enat_bank', name: 'Enat Bank', icon: '🏦', requiresBank: true, requiresPhone: false },
];

export const getPayoutMethod = (id) => {
  return PAYOUT_METHODS.find(m => m.id === id) || PAYOUT_METHODS[0];
};

export const getPayoutMethodName = (id) => {
  const method = getPayoutMethod(id);
  return method ? method.name : 'Bank Transfer';
};